import { useEffect, useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import ConsultationCard from './ConsultationCard'
import { useReviewSummaries } from '../../../hooks/useReviews'
import { isExcludedAstrologer } from '../../../utils/excludedAstrologers'
import {
  DEFAULT_FILTERS,
  EXPERIENCE_OPTIONS,
  MAX_PRICE,
  SORT_OPTIONS,
  SORT_PARAMS,
  decorateConsultation,
} from './consultationsData'

const PER_PAGE = 6

function SkeletonCard() {
  return (
    <div className="rounded-4xl overflow-hidden animate-pulse bg-white border border-[#D7C29A]/25 p-5">
      <div className="flex gap-5">
        <div className="hidden sm:block w-40 shrink-0 rounded-3xl" style={{ height: 180, background: 'rgba(200,146,42,.10)' }} />
        <div className="flex-1 space-y-3 py-1">
          <div className="h-4 w-1/3 rounded" style={{ background: 'rgba(200,146,42,.14)' }} />
          <div className="h-5 w-2/3 rounded" style={{ background: 'rgba(200,146,42,.14)' }} />
          <div className="h-3 w-full rounded" style={{ background: 'rgba(200,146,42,.10)' }} />
          <div className="h-3 w-1/2 rounded" style={{ background: 'rgba(200,146,42,.10)' }} />
          <div className="h-9 w-2/3 rounded-full" style={{ background: 'rgba(200,146,42,.08)' }} />
        </div>
      </div>
    </div>
  )
}

export default function ConsultationsListingSection() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [page, setPage] = useState(1)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [debouncedSearch, setDebouncedSearch] = useState('')

  // Debounce the search box so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search.trim()), 350)
    return () => clearTimeout(t)
  }, [filters.search])

  const setFilter = (key) => (value) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setPage(1)
  }

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS)
    setPage(1)
  }

  // Reference lists for the filter dropdowns — small and rarely changing.
  const { data: skills } = useQuery({
    queryKey: ['skills'],
    queryFn: () => api.lookups.skills(),
    staleTime: Infinity,
  })
  const { data: languages } = useQuery({
    queryKey: ['languages'],
    queryFn: () => api.lookups.languages(),
    staleTime: Infinity,
  })

  const expertiseOptions = useMemo(
    () => [
      { value: 'any', label: 'All specialities' },
      ...(skills ?? []).filter((s) => s.status !== false).map((s) => ({ value: s.name, label: s.name })),
    ],
    [skills],
  )

  const languageOptions = useMemo(
    () => [
      { value: 'any', label: 'All languages' },
      ...(languages ?? []).filter((l) => l.is_active !== false).map((l) => ({ value: l.name, label: l.name })),
    ],
    [languages],
  )

  // The live /astrologers/filter contract: expertise/languages match display
  // names; the slider ceiling means "no price cap", so the param is omitted.
  const params = useMemo(() => {
    const sort = SORT_PARAMS[filters.sort] ?? SORT_PARAMS.rating
    return {
      page,
      per_page: PER_PAGE,
      sort_by: sort.by,
      sort_order: sort.order,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(filters.expertise !== 'any' ? { expertise: [filters.expertise] } : {}),
      ...(filters.language !== 'any' ? { languages: [filters.language] } : {}),
      ...(filters.experience !== 'any' ? { min_experience: Number(filters.experience) } : {}),
      ...(filters.maxPrice < MAX_PRICE ? { max_rate: filters.maxPrice } : {}),
    }
  }, [filters, debouncedSearch, page])

  const {
    data: result,
    isPending,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['astrologersFilter', params],
    queryFn: () => api.astrologers.filter(params),
    placeholderData: keepPreviousData,
  })

  const astrologers = useMemo(
    () => (result?.data ?? []).filter((r) => !isExcludedAstrologer(r)).map(decorateConsultation),
    [result],
  )

  const meta = result?.meta
  const total = meta?.total ?? astrologers.length
  const lastPage = meta?.last_page ?? 1
  const liveRatings = useReviewSummaries('astrologer', astrologers.map((a) => a.id))

  const pageNumbers = useMemo(() => {
    const windowSize = 5
    const start = Math.max(1, Math.min(page - 2, lastPage - windowSize + 1))
    const end = Math.min(lastPage, start + windowSize - 1)
    return Array.from({ length: end - start + 1 }, (_, i) => start + i)
  }, [page, lastPage])

  const goToPage = (n) => {
    setPage(n)
    document.getElementById('consultations-listing')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section id="consultations-listing" className="relative max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12 py-10">
      <div className="grid gap-6 md:px-12">
        {/* Mobile filter bar */}
        <div className="lg:hidden rounded-3xl sm:rounded-4xl border border-[#D7C29A]/30 bg-white/80 backdrop-blur-md p-4 sm:p-5 shadow-[0_20px_55px_rgba(28,20,10,0.05)]">
          <div className="flex flex-col gap-4">
            <div className="relative">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B5A090]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8" />
                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder="Search astrologers"
                className="w-full rounded-full border border-[#D4AF37]/20 bg-white/50 py-3 pl-11 pr-4 text-sm text-[#1C140A] outline-none transition-all focus:border-secondary focus:ring-2 focus:ring-secondary/20"
                value={filters.search}
                onChange={(e) => setFilter('search')(e.target.value)}
              />
            </div>
            {/* `items-end` + a matching h-12 on both controls: the button used
                to centre itself against the taller label+select block, and
                `min-w-40` on the select forced a wrap at 375px. */}
            <div className="flex items-end gap-3">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="inline-flex h-12 shrink-0 items-center gap-2 rounded-full bg-secondary px-5 sm:px-6 text-xs sm:text-sm font-bold uppercase tracking-[0.15em] sm:tracking-[0.2em] whitespace-nowrap text-white shadow-lg shadow-secondary/30 transition-all hover:scale-105 active:scale-95"
              >
                Filters
              </button>
              <div className="min-w-0 flex-1">
                <label className="block truncate text-[10px] font-bold uppercase tracking-[0.3em] text-[#8B7355]">Sort by</label>
                <select
                  className="mt-2 h-12 w-full rounded-2xl border border-[#D4AF37]/20 bg-white/50 px-3 sm:px-4 text-sm text-[#1C140A] outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20"
                  value={filters.sort}
                  onChange={(e) => setFilter('sort')(e.target.value)}
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop filter panel */}
        <div className="hidden lg:block  rounded-[40px] border border-[#D7C29A]/30 bg-white/60 backdrop-blur-lg p-8 shadow-[0_25px_60px_rgba(28,20,10,0.06)]">
          <div className="grid gap-8 xl:grid-cols-[1.9fr_1fr] items-start">
            <div className="grid gap-6 grid-cols-3">
              <div className="space-y-3 col-span-3">
                <label className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#8B7355]">Search</label>
                <div className="relative">
                  <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B5A090]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by name or speciality..."
                    className="w-full rounded-full border border-[#D4AF37]/20 bg-white/50 py-3 pl-11 pr-4 text-sm text-[#1C140A] outline-none transition-all focus:border-secondary focus:ring-2 focus:ring-secondary/20"
                    value={filters.search}
                    onChange={(e) => setFilter('search')(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#8B7355]">Speciality</label>
                <select
                  className="w-full rounded-2xl border border-[#D4AF37]/20 bg-white/50 px-4 py-3 text-sm text-[#1C140A] outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20"
                  value={filters.expertise}
                  onChange={(e) => setFilter('expertise')(e.target.value)}
                >
                  {expertiseOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#8B7355]">Language</label>
                <select
                  className="w-full rounded-2xl border border-[#D4AF37]/20 bg-white/50 px-4 py-3 text-sm text-[#1C140A] outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20"
                  value={filters.language}
                  onChange={(e) => setFilter('language')(e.target.value)}
                >
                  {languageOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#8B7355]">Experience</label>
                <select
                  className="w-full rounded-2xl border border-[#D4AF37]/20 bg-white/50 px-4 py-3 text-sm text-[#1C140A] outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20"
                  value={filters.experience}
                  onChange={(e) => setFilter('experience')(e.target.value)}
                >
                  {EXPERIENCE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-6 border-l border-[#D4AF37]/10 pl-0 xl:pl-8">
              <div className="space-y-3">
                <label className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#8B7355]">Price range</label>
                <div className="rounded-3xl border border-[#D4AF37]/20 bg-[#FEF6E7]/50 p-5">
                  <div className="flex items-center justify-between text-sm text-[#5C3A18] mb-4">
                    <span>Up to</span>
                    <strong>{filters.maxPrice >= MAX_PRICE ? 'Any price' : `₹${filters.maxPrice.toLocaleString('en-IN')}`}</strong>
                  </div>
                  <input
                    type="range"
                    min="500"
                    max={MAX_PRICE}
                    step="100"
                    value={filters.maxPrice}
                    onChange={(e) => setFilter('maxPrice')(Number(e.target.value))}
                    className="w-full h-2 rounded-full appearance-none cursor-pointer bg-[#D7C29A]/30 accent-secondary"
                  />
                </div>
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#8B7355]">Sort by</label>
                <select
                  className="w-full rounded-2xl border border-[#D4AF37]/20 bg-white/50 px-4 py-3 text-sm text-[#1C140A] outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20"
                  value={filters.sort}
                  onChange={(e) => setFilter('sort')(e.target.value)}
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={resetFilters}
                className="w-full rounded-full bg-[#1C140A] px-5 py-3 text-sm font-bold uppercase tracking-[0.2em] text-white transition-all hover:bg-secondary hover:shadow-lg hover:shadow-secondary/20"
              >
                Reset filters
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-8 pt-8 md:px-12">
        <div className="w-full space-y-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm uppercase tracking-[0.35em] text-[#8B7355]">Showing</p>
              <h2 className="mt-3 text-2xl font-cinzel text-[#1C140A]">
                {isPending ? 'Loading astrologers…' : `${total} astrologer${total === 1 ? '' : 's'} available`}
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm text-[#5C3A18]">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/20 bg-[#FEF8E6] px-4 py-2">Video Call</span>
              <span className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/20 bg-[#FEF8E6] px-4 py-2">Verified Experts</span>
              <span className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/20 bg-[#FEF8E6] px-4 py-2">24 × 7</span>
            </div>
          </div>

          {isPending ? (
            <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
              {Array.from({ length: PER_PAGE }, (_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : isError ? (
            <div className="rounded-4xl border border-[#D7C29A]/25 bg-white p-10 text-center shadow-[0_20px_55px_rgba(28,20,10,0.08)]">
              <div className="mx-auto mb-6 grid h-28 w-28 place-items-center rounded-full bg-secondary/10 text-secondary text-4xl">🛰️</div>
              <h3 className="text-2xl font-cinzel text-[#1C140A]">Couldn’t load astrologers</h3>
              <p className="mt-3 text-sm text-[#8B7355]">Something went wrong while fetching live data. Please try again.</p>
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-8 inline-flex items-center justify-center rounded-full bg-[#1C140A] px-7 py-3 text-sm font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-[#3A1E0C]"
              >
                Retry
              </button>
            </div>
          ) : astrologers.length > 0 ? (
            <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
              {astrologers.map((item) => (
                <ConsultationCard key={item.id} item={item} liveRating={liveRatings[item.id]} />
              ))}
            </div>
          ) : (
            <div className="rounded-4xl border border-[#D7C29A]/25 bg-white p-10 text-center shadow-[0_20px_55px_rgba(28,20,10,0.08)]">
              <div className="mx-auto mb-6 grid h-28 w-28 place-items-center rounded-full bg-secondary/10 text-secondary text-4xl">🔮</div>
              <h3 className="text-2xl font-cinzel text-[#1C140A]">No astrologers match your filters</h3>
              <p className="mt-3 text-sm text-[#8B7355]">Try a broader search, adjust the speciality, or reset the filters to view more experts.</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-8 inline-flex items-center justify-center rounded-full bg-[#1C140A] px-7 py-3 text-sm font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-[#3A1E0C]"
              >
                Reset Filters
              </button>
            </div>
          )}

          {!isPending && !isError && total > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-4xl border border-[#D7C29A]/25 bg-white p-5 shadow-[0_20px_55px_rgba(28,20,10,0.08)]">
              <div className="text-sm text-[#5C3A18]">
                Showing <span className="font-semibold text-[#1C140A]">{astrologers.length}</span> of{' '}
                <span className="font-semibold text-[#1C140A]">{total}</span> results
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="pagination-link"
                  disabled={page <= 1}
                  onClick={() => goToPage(page - 1)}
                >
                  Previous
                </button>
                {pageNumbers.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`pagination-link${n === page ? ' active' : ''}`}
                    onClick={() => goToPage(n)}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  className="pagination-link"
                  disabled={page >= lastPage}
                  onClick={() => goToPage(page + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile filter drawer */}
      <div className="filter-panel" aria-hidden={!drawerOpen}>
        <div
          className={`filter-panel-backdrop${drawerOpen ? ' visible' : ''}`}
          onClick={() => setDrawerOpen(false)}
        />
        <div className={`filter-panel-drawer${drawerOpen ? ' open' : ''}`}>
          <div className="flex items-center justify-between border-b border-[#D4AF37]/20 p-5">
            <div>
              <p className="text-sm uppercase tracking-[0.35em] text-[#8B7355]">Filters</p>
              <p className="text-xs text-[#5C3A18]">Refine astrologers on mobile</p>
            </div>
            <button type="button" onClick={() => setDrawerOpen(false)} className="text-[#1C140A] rounded-full p-2 hover:bg-[#F5E7C9]">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="space-y-4 p-5">
            <div className="space-y-3">
              <label className="text-[10px] uppercase tracking-[0.4em] text-[#8B7355]">Search</label>
              <input
                type="text"
                placeholder="Search astrologers"
                className="w-full rounded-3xl border border-[#D4AF37]/20 bg-white px-4 py-3 text-sm text-[#1C140A] outline-none focus:border-secondary"
                value={filters.search}
                onChange={(e) => setFilter('search')(e.target.value)}
              />
            </div>
            <div className="space-y-3">
              <label className="text-[10px] uppercase tracking-[0.4em] text-[#8B7355]">Speciality</label>
              <select
                className="w-full rounded-3xl border border-[#D4AF37]/20 bg-white px-4 py-3 text-sm text-[#1C140A] outline-none focus:border-secondary"
                value={filters.expertise}
                onChange={(e) => setFilter('expertise')(e.target.value)}
              >
                {expertiseOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-3">
              <label className="text-[10px] uppercase tracking-[0.4em] text-[#8B7355]">Language</label>
              <select
                className="w-full rounded-3xl border border-[#D4AF37]/20 bg-white px-4 py-3 text-sm text-[#1C140A] outline-none focus:border-secondary"
                value={filters.language}
                onChange={(e) => setFilter('language')(e.target.value)}
              >
                {languageOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-3">
              <label className="text-[10px] uppercase tracking-[0.4em] text-[#8B7355]">Experience</label>
              <select
                className="w-full rounded-3xl border border-[#D4AF37]/20 bg-white px-4 py-3 text-sm text-[#1C140A] outline-none focus:border-secondary"
                value={filters.experience}
                onChange={(e) => setFilter('experience')(e.target.value)}
              >
                {EXPERIENCE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-[0.4em] text-[#8B7355]">Sort</label>
              <select
                className="w-full rounded-3xl border border-[#D4AF37]/20 bg-white px-4 py-3 text-sm text-[#1C140A] outline-none focus:border-secondary"
                value={filters.sort}
                onChange={(e) => setFilter('sort')(e.target.value)}
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="w-full rounded-full bg-[#1C140A] px-5 py-3 text-sm font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-[#3A1E0C]"
            >
              Apply filters
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}
